import React from 'react';
import { createStructuredSelector } from 'reselect';
import { connect, ConnectedProps } from 'react-redux';

// Services
import useFetchConversationHistories from '../state/useFetchConversationHistory';

import useIsMountedRef from '../../../../../../utils/react/useIsMountedRef';

// Selectors
import { getCurrentBoard, getCurrentBoardId } from '../../../../../../reducers/currentBoardId/currentBoardIdSelector';

// Components
import ToolbarPopup from '../../../toolbarPopup/ToolbarPopup';
import Spinner from '../../../../../../components/loaders/Spinner';
import AiAssistantPopupResults from './resultSection/AiAssistantPopupResults';
import AiAssistantPromptSection from './promptSection/AiAssistantPromptSection';

// Types
import { UseAiToolState } from '../state/aiToolStateTypes';
import { StreamRequestHandlers } from '../state/useSubmitAiStreamRequestHandlers';

// Constants
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';
import { ModalIds } from '../../../../../../components/modal/modalConstants';

import './AiAssistantPopup.scss';

const aiPopupSelector = createStructuredSelector({
    currentBoardId: getCurrentBoardId,
    currentBoard: getCurrentBoard,
});

const connector = connect(aiPopupSelector);
type PropsFromRedux = ConnectedProps<typeof connector>;
export interface AiPopupProps extends PropsFromRedux, UseAiToolState, StreamRequestHandlers {
    activeModalId: ModalIds;
}

const AiPopupContent = (props: AiPopupProps): React.ReactElement => {
    const { currentConversation } = props;

    useFetchConversationHistories(props);

    // This ref is used to determine whether the delay should be applied when setting focus to the prompt input
    // field or not. The delay is needed to ensure that CSS transitions play correctly, otherwise setting focus
    // immediately to the new text area will cause the CSS transition to immediately finish.
    // However, when the AI popup is first opened, setting the focus on delay feels strange and results in a
    // noticeable flash when the text area gains focus.
    // To avoid this, we set focus immediately on popup open. We can detect this scenario because the following
    // ref will be false on the initial mount at the point that the text area input is checking it.
    const isAiPopupMountedRef = useIsMountedRef();

    if (!currentConversation) {
        return (
            <div className="AiAssistantPopup">
                <div className="assistant-popup-section loading">
                    <Spinner show />
                </div>
            </div>
        );
    }

    return (
        <div className="AiAssistantPopup">
            <div className="assistant-popup-section assistant-popup-prompt-section">
                <AiAssistantPromptSection {...props} isAiPopupMountedRef={isAiPopupMountedRef} />
            </div>
            <div className="assistant-popup-section assistant-popup-results">
                <AiAssistantPopupResults
                    {...props}
                    fetched={currentConversation?.fetched || false}
                    fetching={currentConversation?.fetching || false}
                    results={currentConversation?.results || []}
                />
            </div>
        </div>
    );
};

const ConnectedAiPopupContent = connector(AiPopupContent);

const AiPopup = (props: AiPopupProps): React.ReactElement => {
    const keepAiPopupOpen = props.activeModalId === ModalIds.AI_REPORT_FEEDBACK;

    return (
        <ToolbarPopup
            {...props}
            popupId={PopupIds.AI_ASSISTANT}
            buttonSelector=".popup-trigger-ai-assistant"
            className="ai-assistant"
            stayOpen={keepAiPopupOpen}
            ignoredClicksSelector=".AiAssistantPopup, .AiAssistantReportFeedbackModal"
        >
            <ConnectedAiPopupContent {...props} />
        </ToolbarPopup>
    );
};

export default AiPopup;
